import { createContext, useContext } from "react";
import { COMPONENT_CATALOG, COMPONENT_GROUPS } from "./data/catalog";
import { MAP_DEFINITIONS } from "./data/maps";
import { SCENARIOS } from "./data/scenarios";
import type { MapDefinition } from "./domain/types";

export type LanguageMode = "plain" | "technical";

const messages = {
    plain: {
        appName: "Datacenter Planner",
        unsaved: "Changes not downloaded",
        exported: "Download up to date",
        noPlanOpen: "No plan open",
        createOrImport: "Create a plan or open a saved copy",
        project: "Project",
        newPlan: "New Plan",
        undo: "Undo",
        redo: "Redo",
        importPlan: "Open Plan",
        importSavegame: "Open Uptime Save",
        importFromApi: "Import from API",
        beta: "Beta",
        importSavegameActionHint: "Build a plan from a local game save",
        importFromApiActionHint: "Read the datacenter from your running game",
        importSavegameSubtitle: "Read racks and equipment from an Uptime .save file",
        importApiSubtitle: "Connect to the read-only local API of the running game",
        apiAddress: "Game API address",
        apiAddressHint: "The default address works when Uptime runs on this computer.",
        apiScenarioRequired: "The game API does not report the scenario. Choose the scenario for this plan.",
        connectToGame: "Connect to Game",
        retryApiImport: "Try Again",
        apiStageConnecting: "Connecting to Uptime",
        apiStageWorld: "Reading the active location",
        apiStageRacks: "Reading racks",
        apiStageEquipment: "Reading installed equipment",
        apiStageMapping: "Building the plan",
        apiStageComplete: "Live snapshot ready",
        apiImportFailed: "The running game could not be imported",
        apiErrorInvalidUrl: "Enter a valid HTTP or HTTPS API address without a path.",
        apiErrorUnreachable: "The local API could not be reached. Check that Uptime is open and its API is enabled.",
        apiErrorHttp: "The local API returned an error. The game may not have published a world yet.",
        apiErrorInvalidResponse: "The local API returned data this planner does not understand.",
        apiErrorTooLarge: "The local API contains more equipment than the planner can safely import.",
        useApiSnapshot: "Import Live Snapshot",
        apiImported: "Running Uptime game imported.",
        localGameApi: "Local game API",
        processedLocally: "Stays on this device",
        processingSavegame: "Reading your save",
        processingApi: "Reading your running game",
        savegameReady: "Ready to import",
        savegameStageReading: "Loading file",
        savegameStageDecoding: "Finding racks and equipment",
        savegameStageMapping: "Matching equipment",
        savegameStageValidating: "Checking rack positions",
        savegameStageComplete: "Import preview complete",
        savegameBackgroundHint: "Large saves are processed away from the page, so the planner stays responsive.",
        savegameWorkerFailed: "The background save reader stopped unexpectedly.",
        savegameImportFailed: "This save could not be opened",
        savegameErrorEmptyFile: "The selected save is empty.",
        savegameErrorFileTooLarge: "This save is larger than the 256 MB safety limit.",
        savegameErrorReadFailed: "The selected save could not be read completely.",
        savegameErrorUnsupportedFormat: "This is not a supported Uptime save format.",
        savegameErrorUnsupportedSite: "The save uses a site layout that is not available in this planner.",
        savegameErrorMappingFailed: "The racks and equipment in this save could not be converted into a plan.",
        useSavegame: "Use This Save",
        detectedMap: "Detected layout",
        matchedRacks: "Matched racks",
        importedEquipment: "Equipment found",
        saveFormat: "Save format",
        importSource: "Import source",
        servers: "Servers",
        switches: "Switches",
        gateways: "Gateways",
        securityDevices: "Security equipment",
        patchPanels: "Patch panels",
        fixedEquipmentDetected: "built-in connection devices were matched to the room layout.",
        equipmentSkipped: "items could not be added",
        equipmentSkippedHint: "Review every skipped equipment type and reason below.",
        skipUnsupportedSku: "Equipment type is not known yet",
        skipRackNotFound: "Rack could not be matched",
        skipSizeMismatch: "Saved rack height differs from the catalog",
        skipRackIncompatible: "Equipment is not supported by this rack",
        skipPositionConflict: "Saved rack position is occupied or invalid",
        savegameReplacesPlan: "Using this save replaces the current plan. Nothing changes until you confirm.",
        selectSavegameFile: "Choose an Uptime file with the .save extension.",
        savegameImported: "Uptime save imported.",
        exportPlan: "Download Plan",
        plainView: "Plain View",
        technicalView: "Technical View",
        viewMode: "Language detail",
        localWorkspace: "Private planning workspace",
        emptyTitle: "Plan your datacenter.",
        emptyIntro: "Choose a room layout, fill the racks, and download a copy that you can open again later.",
        featurePositions: "Ready-made rack positions",
        featureSafety: "Safe equipment placement",
        featureStorage: "Saved in this browser",
        projectManagement: "Project",
        projectManagementSubtitle: "Create, open, download, or rename your plan",
        activePlan: "Current plan",
        autosaved: "Saved on this device",
        officialMap: "Room layout",
        scenario: "Scenario",
        scenarioAvailabilityHint: "Controls which equipment is available",
        changeScenario: "Change scenario",
        changeScenarioHint: "Changes the equipment shown in the catalog. Equipment already in the plan stays in place.",
        searchScenarios: "Search scenarios…",
        rackCapacity: "Available rack space",
        userEquipment: "Added equipment",
        lastChange: "Last changed",
        planActions: "Plan options",
        planActionsHint: "Start another plan or manage the current one.",
        newProjectActions: "Start or open a project",
        newProjectActionsHint: "Create an empty plan or build one from existing data.",
        currentProjectActions: "Current project",
        currentProjectActionsHint: "Manage and download the plan that is currently open.",
        dismissNotification: "Dismiss notification",
        chooseMap: "Choose a room layout",
        validateOpen: "Check and open a saved plan",
        downloadPortable: "Save a copy to your computer",
        updateTitle: "Change the plan name",
        portableCopy: "Downloadable plan copy",
        portableCopyHint: "This browser keeps your work safe. Download a copy to move or back up the plan.",
        resetPlacements: "Remove added equipment",
        fixedProtected: "Equipment supplied by the room layout will stay in place.",
        resetPlan: "Clear Equipment",
        catalogEyebrow: "Available equipment",
        catalogTitle: "Equipment",
        catalogHint: "Drag equipment into the rack · press Enter for exact placement",
        searchComponents: "Search equipment…",
        filterCategories: "Show equipment category",
        allComponents: "All",
        noComponents: "No equipment matches your search.",
        placeWithoutDrag: "Add without dragging",
        floorplanZoomControls: "Map zoom controls",
        currentZoom: "Current zoom",
        resetView: "Reset view",
        panZoomFloorplan: "Move and zoom the map",
        rackStatusLegend: "Rack status",
        free: "Empty",
        partial: "In use",
        full: "Full",
        openRackHint: "Choose a rack to see and edit its equipment",
        item: "item",
        items: "items",
        component: "component",
        components: "components",
        rackHeader: "Rack · highest slot at the top",
        rackScale: "Rack row height",
        used: "used",
        available: "free",
        previousRack: "Previous rack",
        nextRack: "Next rack",
        addEquipment: "Add",
        closeRack: "Close rack",
        top: "TOP",
        wallRack: "Wall rack",
        rackDropArea: "equipment placement area",
        fixedEquipment: "Built-in equipment",
        fixed: "Fixed",
        startsAt: "starts at",
        placeAt: "Add at",
        invalidPosition: "This position is not available",
        rackUnit: "rack space",
        rackUnits: "rack spaces",
        selectedEquipment: "Selected equipment",
        actionsFor: "Options for",
        closeActions: "Close equipment options",
        localName: "Equipment name",
        move: "Move",
        duplicate: "Make a copy",
        remove: "Remove",
        removeSelectedComponents: "Remove selected equipment?",
        removeSelectedComponentsHint:
            "This removes the selected equipment from the rack. You can restore it with Undo.",
        selectedForRemoval: "selected for removal",
        confirmRemove: "Remove selected",
        closeDialog: "Close window",
        select: "Choose…",
        noMatchingOptions: "No matching choices",
        placeComponent: "Add equipment",
        placeSubtitle: "Choose the equipment, rack, and free position",
        cancel: "Cancel",
        category: "Equipment category",
        officialComponent: "Equipment type",
        targetRack: "Rack",
        startRackUnit: "Starting position",
        optional: "Optional",
        positionAvailable: "This position is free",
        cannotPlace: "This equipment does not fit here",
        destinationAvailable: "This position is free",
        cannotMove: "This equipment cannot move here",
        moveComponent: "Move equipment",
        compatibleRack: "choose a rack with enough free space",
        moveTo: "Move to",
        searchCategories: "Search categories…",
        searchOfficialComponents: "Search equipment types…",
        searchRacks: "Search racks…",
        thisRack: "this rack",
        createNewPlan: "Create a new plan",
        newPlanSubtitle: "The room layout supplies the rack positions and any built-in equipment.",
        planName: "Plan name",
        createPlan: "Create Plan",
        searchMaps: "Search room layouts…",
        fixedRackPositions: "rack positions",
        totalRackUnits: "total rack spaces",
        renamePlan: "Rename plan",
        renameRack: "Rename rack",
        rackSettings: "Rack settings",
        rackSettingsSubtitle: "Configure the rack name, height, and equipment-label behavior.",
        rackName: "Rack name",
        rackHeight: "Rack height",
        rackHeightHint: "Choose the physical height used for placement and capacity calculations.",
        rackHeightImportedHint: "The rack height comes from the API or savegame and cannot be changed.",
        rackHeightFixedHint: "This rack type has a fixed height.",
        rackHeightContentConflict: "Equipment above the selected height must be moved or removed first.",
        showRackNameAsComponentPrefix: "Show rack name as equipment-name prefix",
        rackNamePrefixHint: "Only equipment with its own name shows this prefix.",
        localComponentName: "Equipment name",
        optionalLocalName: "Optional equipment name",
        saveChanges: "Save changes",
        appBrand: "Uptime",
        fileTypeMark: "UTP",
        created: "created",
        racks: "racks",
        planNamePlaceholder: "My Datacenter",
        localNamePlaceholder: "e.g. rack-01-device-01",
        small: "Small",
        normal: "Normal",
        labels: "Details",
        colorOverlay: "Overlay",
        interactiveFloorplan: "interactive rack map",
        rackDesigner: "rack editor",
        rackUnitsUsed: "rack spaces used",
        brandMark: "up",
        versionPrefix: "v",
        selected: "selected",
        copied: "copied",
        pasted: "pasted",
        multiSelectHint: "Drag across equipment to select · Ctrl+C / Ctrl+V",
        pasteUnavailable: "No matching free space for this selection",
        quickPlacementUnavailable: "No suitable free space remains in this rack.",
        datacenterCapacity: "Capacity overview",
        capacityScope: "Capacity scope",
        capacityScopeSearch: "Search locations…",
        entireDatacenter: "Entire datacenter",
        allAvailabilityZones: "All racks and availability zones",
        rackPerformance: "Rack stats",
        thermalLoad: "Heat output",
        powerLoad: "Power draw",
        computeCapacity: "Compute",
        memoryCapacity: "Memory",
        storageCapacity: "Storage",
        storageIops: "Storage speed",
        gpuCapacity: "Graphics cards",
        gpuMemory: "Graphics memory",
        inspectionCapacity: "Security capacity",
        storageSuffix: "storage",
        inspectionSuffix: "inspection",
    },
    technical: {
        appName: "Datacenter Planner",
        unsaved: "Not exported",
        exported: "Exported",
        noPlanOpen: "No plan open",
        createOrImport: "Create or import a local plan",
        project: "Project Manager",
        newPlan: "New Plan",
        undo: "Undo",
        redo: "Redo",
        importPlan: "Import .utp",
        importSavegame: "Import Uptime Save",
        importFromApi: "Import from API",
        beta: "Beta",
        importSavegameActionHint: "Parse racks and mounted hardware from .save",
        importFromApiActionHint: "Build a plan from the running local simulation",
        importSavegameSubtitle: "Client-side CSIM import with rack and component validation",
        importApiSubtitle: "Read racks and mounted hardware from the local Uptime API",
        apiAddress: "Local API endpoint",
        apiAddressHint: "Defaults to Uptime's read-only API on this machine.",
        apiScenarioRequired: "The local API does not expose a scenario ID. Select the scenario for this plan.",
        connectToGame: "Connect to Game",
        retryApiImport: "Retry Connection",
        apiStageConnecting: "Connecting to local API",
        apiStageWorld: "Reading world metadata",
        apiStageRacks: "Loading rack inventory",
        apiStageEquipment: "Loading mounted components",
        apiStageMapping: "Mapping API inventory",
        apiStageComplete: "API snapshot processed",
        apiImportFailed: "Local API import failed",
        apiErrorInvalidUrl: "Enter a valid HTTP or HTTPS origin without credentials, path, query, or fragment.",
        apiErrorUnreachable:
            "Could not reach the local Uptime API. Verify that the game is running and the API is enabled.",
        apiErrorHttp: "The local API returned an error response. Uptime may not have published a world yet.",
        apiErrorInvalidResponse: "The local API response does not match the supported Uptime API contract.",
        apiErrorTooLarge: "The API snapshot exceeds the planner's 10,000-component safety limit.",
        useApiSnapshot: "Import API Snapshot",
        apiImported: "Local Uptime API snapshot imported.",
        localGameApi: "Local Uptime API",
        processedLocally: "Processed locally",
        processingSavegame: "Processing savegame",
        processingApi: "Processing API snapshot",
        savegameReady: "Import preview ready",
        savegameStageReading: "Reading binary save",
        savegameStageDecoding: "Decoding CSIM structures",
        savegameStageMapping: "Mapping hardware definitions",
        savegameStageValidating: "Validating rack occupancy",
        savegameStageComplete: "Savegame processed",
        savegameBackgroundHint: "Parsing runs in a dedicated browser worker. The file never leaves this device.",
        savegameWorkerFailed: "The savegame import worker stopped unexpectedly.",
        savegameImportFailed: "Savegame import failed",
        savegameErrorEmptyFile: "The selected .save file contains no data.",
        savegameErrorFileTooLarge: "The selected .save file exceeds the 256 MB client-side safety limit.",
        savegameErrorReadFailed: "The browser could not read the complete .save file.",
        savegameErrorUnsupportedFormat: "The file is not a supported CSIM5-CSIM9 Uptime save.",
        savegameErrorUnsupportedSite: "The save references a site that has no matching planner map.",
        savegameErrorMappingFailed: "The decoded rack and hardware data could not be mapped to planner definitions.",
        useSavegame: "Import Savegame",
        detectedMap: "Detected map",
        matchedRacks: "Matched racks",
        importedEquipment: "Imported components",
        saveFormat: "Save format",
        importSource: "Import source",
        servers: "Servers",
        switches: "Switches",
        gateways: "Gateways",
        securityDevices: "Security devices",
        patchPanels: "Patch panels",
        fixedEquipmentDetected: "fixed EDD components were resolved from the official map.",
        equipmentSkipped: "components skipped",
        equipmentSkippedHint: "Every skipped SKU is listed below together with its validation reason.",
        skipUnsupportedSku: "Unsupported SKU",
        skipRackNotFound: "Target rack not found",
        skipSizeMismatch: "SKU rack-height mismatch",
        skipRackIncompatible: "Incompatible rack type",
        skipPositionConflict: "Rack-unit collision or invalid position",
        savegameReplacesPlan: "Importing this snapshot replaces the active local plan after confirmation.",
        selectSavegameFile: "Select a file with the .save extension.",
        savegameImported: "Uptime savegame imported.",
        exportPlan: "Export .utp",
        plainView: "Plain View",
        technicalView: "Technical View",
        viewMode: "Terminology mode",
        localWorkspace: "Local planning workspace",
        emptyTitle: "Build your next datacenter.",
        emptyIntro: "Choose an official Uptime map, plan each 19-inch rack, and export the versioned .utp document.",
        featurePositions: "Map-defined rack coordinates",
        featureSafety: "Collision-safe placement",
        featureStorage: "Local browser persistence",
        projectManagement: "Project management",
        projectManagementSubtitle: "Local workspace, portable files, and plan metadata",
        activePlan: "Active local plan",
        autosaved: "Autosaved locally",
        officialMap: "Official map",
        scenario: "Scenario",
        scenarioAvailabilityHint: "Filters hardware by Wiki availability",
        changeScenario: "Change scenario",
        changeScenarioHint: "Updates catalog availability. Existing placements remain unchanged.",
        searchScenarios: "Search scenarios…",
        rackCapacity: "Rack capacity",
        userEquipment: "User components",
        lastChange: "Last mutation",
        planActions: "Project operations",
        planActionsHint: "Create another plan or manage the current portable workspace.",
        newProjectActions: "New project",
        newProjectActionsHint: "Create a plan or import one from a UTP file, savegame, or the local API.",
        currentProjectActions: "Open project",
        currentProjectActionsHint: "Edit or export the active planner document.",
        dismissNotification: "Dismiss notification",
        chooseMap: "Choose an official map",
        validateOpen: "Validate and import .utp",
        downloadPortable: "Serialize a portable snapshot",
        updateTitle: "Update project metadata",
        portableCopy: "Portable .utp document",
        portableCopyHint: "Browser persistence protects this device. Export the plan for transfer or external backup.",
        resetPlacements: "Reset user placements",
        fixedProtected: "Fixed map equipment remains immutable and protected.",
        resetPlan: "Reset Plan",
        catalogEyebrow: "Official definitions",
        catalogTitle: "Component Catalog",
        catalogHint: "Read-only definitions · drag to a rack · press Enter for exact placement",
        searchComponents: "Search components…",
        filterCategories: "Filter components by category",
        allComponents: "All",
        noComponents: "No official components match the active filters.",
        placeWithoutDrag: "Place without drag and drop",
        floorplanZoomControls: "Floorplan zoom controls",
        currentZoom: "Current zoom level",
        resetView: "Reset",
        panZoomFloorplan: "Pan and zoom floorplan",
        rackStatusLegend: "Rack occupancy legend",
        free: "Free",
        partial: "Partially occupied",
        full: "Full",
        openRackHint: "Select a rack to open the 19-inch front view",
        item: "item",
        items: "items",
        component: "component",
        components: "components",
        rackHeader: "19-inch rack · highest U at top",
        rackScale: "Rack unit scale",
        used: "used",
        available: "available",
        previousRack: "Previous rack",
        nextRack: "Next rack",
        addEquipment: "Place",
        closeRack: "Close rack designer",
        top: "TOP",
        wallRack: "Wall rack",
        rackDropArea: "equipment drop area",
        fixedEquipment: "fixed map equipment",
        fixed: "Fixed",
        startsAt: "starts U",
        placeAt: "Place at",
        invalidPosition: "Invalid position",
        rackUnit: "rack unit",
        rackUnits: "rack units",
        selectedEquipment: "Selected component",
        actionsFor: "Actions for",
        closeActions: "Close component actions",
        localName: "Local name",
        move: "Move",
        duplicate: "Duplicate",
        remove: "Remove",
        removeSelectedComponents: "Remove selected components?",
        removeSelectedComponentsHint:
            "This removes the selected components from the rack. You can restore them with Undo.",
        selectedForRemoval: "selected for removal",
        confirmRemove: "Remove selected",
        closeDialog: "Close dialog",
        select: "Select…",
        noMatchingOptions: "No matching options",
        placeComponent: "Place component",
        placeSubtitle: "Keyboard-accessible alternative to drag and drop",
        cancel: "Cancel",
        category: "Component category",
        officialComponent: "Official component",
        targetRack: "Target rack",
        startRackUnit: "Start rack unit",
        optional: "Optional",
        positionAvailable: "Position available",
        cannotPlace: "Cannot place here",
        destinationAvailable: "Destination available",
        cannotMove: "Cannot move here",
        moveComponent: "Move component",
        compatibleRack: "choose any compatible rack",
        moveTo: "Move to",
        searchCategories: "Search categories…",
        searchOfficialComponents: "Search official components…",
        searchRacks: "Search racks…",
        thisRack: "this rack",
        createNewPlan: "Create a new plan",
        newPlanSubtitle: "Rack positions and fixed equipment come from the selected official map.",
        planName: "Plan name",
        createPlan: "Create Plan",
        searchMaps: "Search official maps…",
        fixedRackPositions: "fixed rack positions",
        totalRackUnits: "total rack units",
        renamePlan: "Rename plan",
        renameRack: "Rename rack",
        rackSettings: "Rack settings",
        rackSettingsSubtitle: "Configure rack identity, rack units, and component-label behavior.",
        rackName: "Rack name",
        rackHeight: "Rack height",
        rackHeightHint: "The selected rack units drive placement validation and capacity calculations.",
        rackHeightImportedHint: "Rack units are authoritative from the API or savegame and cannot be changed.",
        rackHeightFixedHint: "This rack type has a fixed rack-unit capacity.",
        rackHeightContentConflict: "Components above the selected rack boundary must be moved or removed first.",
        showRackNameAsComponentPrefix: "Show rack name as component-name prefix",
        rackNamePrefixHint: "The prefix is rendered only on components that have a local name.",
        localComponentName: "Local component name",
        optionalLocalName: "Optional local name",
        saveChanges: "Save changes",
        appBrand: "Uptime",
        fileTypeMark: "UTP",
        created: "created",
        racks: "racks",
        planNamePlaceholder: "My Datacenter",
        localNamePlaceholder: "e.g. r1-1-arm-01",
        small: "Small",
        normal: "Normal",
        labels: "Labels",
        colorOverlay: "Overlay",
        interactiveFloorplan: "interactive rack floorplan",
        rackDesigner: "rack designer",
        rackUnitsUsed: "rack units used",
        brandMark: "up",
        versionPrefix: "v",
        selected: "selected",
        copied: "copied",
        pasted: "pasted",
        multiSelectHint: "Drag across components to select · Ctrl+C / Ctrl+V",
        pasteUnavailable: "No compatible free space for this selection",
        quickPlacementUnavailable: "No compatible free space remains in this rack.",
        datacenterCapacity: "Capacity overview",
        capacityScope: "Capacity scope",
        capacityScopeSearch: "Search capacity scopes…",
        entireDatacenter: "Datacenter",
        allAvailabilityZones: "All racks and availability zones",
        rackPerformance: "Rack stats",
        thermalLoad: "Thermal load",
        powerLoad: "Power draw",
        computeCapacity: "Compute",
        memoryCapacity: "Memory",
        storageCapacity: "Storage",
        storageIops: "Storage IOPS",
        gpuCapacity: "GPU",
        gpuMemory: "GPU memory",
        inspectionCapacity: "Inspection",
        storageSuffix: "storage",
        inspectionSuffix: "inspection",
    },
} as const;

export type MessageKey = keyof (typeof messages)["plain"];

export function translate(mode: LanguageMode, key: MessageKey): string {
    return messages[mode][key];
}

export type EntityKind =
    | "map"
    | "scenario"
    | "room"
    | "rack"
    | "zone"
    | "area"
    | "badge"
    | "label"
    | "category"
    | "group"
    | "component"
    | "handle"
    | "fixed";

type EntityTranslations = Readonly<Record<string, string>>;

function createDeveloperEntityTranslations(): EntityTranslations {
    const translations: Record<string, string> = {};
    for (const component of COMPONENT_CATALOG) {
        translations[`component.${component.id}`] = component.name;
        translations[`handle.${component.id}`] = component.shortHandle;
        translations[`category.${component.category.toLowerCase()}`] = component.category;
    }
    for (const scenario of SCENARIOS) {
        translations[`scenario.${scenario.id}`] = scenario.name;
    }
    for (const group of COMPONENT_GROUPS) {
        translations[`group.${group.id}`] = group.name;
    }
    for (const map of MAP_DEFINITIONS as readonly MapDefinition[]) {
        translations[`map.${map.id}`] = map.name;
        translations[`room.${map.id}`] = map.room.label;
        for (const rack of map.racks) {
            translations[`rack.${rack.id}`] = rack.name;
            for (const fixed of rack.fixedComponents ?? []) {
                if (fixed.localName !== undefined) {
                    translations[`fixed.${fixed.id}`] = fixed.localName;
                }
            }
        }
        for (const zone of map.zones) {
            translations[`zone.${zone.id}`] = zone.name;
        }
        for (const area of map.areas) {
            translations[`area.${area.id}`] = area.name;
        }
        for (const badge of map.badges) {
            translations[`badge.${badge.id}`] = badge.name;
        }
        for (const label of map.labels) {
            translations[`label.${label.id}`] = label.name;
        }
    }
    return Object.freeze(translations);
}

const developerEntityTranslations = createDeveloperEntityTranslations();

// Every developer-defined label reaches the UI through this table. Entries may be
// overridden independently per terminology mode without changing stable domain IDs.
const sharedEntityTranslations: EntityTranslations = Object.freeze({
    ...developerEntityTranslations,
    "map.basement": "Basement",
    "map.small-colo": "Small Colo",
    "room.basement": "Basement facility",
    "room.small-colo": "Small Colo facility",
    "rack.basement-rack-01": "Basement R1",
    "rack.basement-rack-02": "Basement R2",
    "rack.basement-rack-03": "Basement R3",
    "rack.basement-rack-04": "Basement R4",
    "rack.basement-wall-rack-01": "Wall Rack W1",
    "fixed.basement-wall-rack-01-fixed-edd": "Basement EDD",
    "fixed.small-colo-az2-fixed-edd": "AZ2 EDD",
    "fixed.small-colo-az1-fixed-edd": "AZ1 EDD",
    "category.servers": "Servers",
    "category.switches": "Switches",
    "category.gateways": "Gateways",
    "category.security": "Security",
    "category.edd": "EDD",
});

// Fill these values with beginner-friendly component names. Empty entries
// intentionally fall back to the official technical name from the catalog.
export const PLAIN_COMPONENT_NAME_TRANSLATIONS: EntityTranslations = Object.freeze({
    "component.arm-1u": "Low-Power Server",
    "component.balanced-x86-1u": "Everyday Server (1U)",
    "component.storage-1u": "Storage Server (1U)",
    "component.compute-dense-1u": "Fast-CPU Server",
    "component.arm-2u": "Low-Power Server (2U)",
    "component.arm-dual-socket-2u": "Low-Power Server (Dual)",
    "component.balanced-x86-2u": "Everyday Server (2U)",
    "component.compute-dense-2u": "Fast-CPU Server (2U)",
    "component.compute-dual-socket-2u": "Fast-CPU Server (Dual)",
    "component.storage-2u": "Storage Server (2U)",
    "component.memory-optimised-2u": "High-Memory Server",
    "component.memory-optimised-dual-socket-2u": "High-Memory Server (Dual)",
    "component.gpu-2x-2u": "Graphics Server (2 cards)",
    "component.risc-v-2u": "Open-Chip Server",
    "component.storage-4u": "Big-Storage Server (4U)",
    "component.gpu-4x-4u": "Graphics Server (4 cards)",
    "component.risc-v-4u": "Open-Chip Server (4U)",
    "component.mainframe-4u": "Big-Iron Server",
    "component.storage-7u": "Bulk-Storage Server (7U)",
    "component.gpu-8x-7u": "Graphics Server (8cards, 7U)",
    "component.mainframe-7u": "Big-Iron Server (Flagship)",
    "component.switch-16x1g-rj45-managed-l2": "Rack Switch (16 ports) Managed L2",
    "component.switch-24x1g-rj45-managed-l2": "Rack Switch (24 ports) Managed L2",
    "component.switch-24x1g-rj45-2x10g-sfp-plus-managed-l2": "Rack Switch + Fast Uplinks - Managed L2",
    "component.switch-48x1g-rj45-4x10g-sfp-plus-managed-l2": "Rack Switch + Fast Uplinks (48 ports) - Managed L2",
    "component.switch-24x10g-sfp-plus-2x25g-sfp28-managed-l3": "Fast Rack Switch (24 ports + uplinks) - Managed L3",
    "component.switch-36x10g-sfp-plus-2x25g-sfp28-managed-l3": "Fast Rack Switch (36 ports + uplinks) - Managed L3",
    "component.switch-48x10g-sfp-plus-managed-l3": "Fast Rack Switch (48 ports) - Managed L3",
    "component.switch-48x10g-sfp-plus-4x40g-qsfp-plus-managed-l3":
        "Fast Rack Switch (48 ports + fast uplinks) - Managed L3",
    "component.switch-48x25g-sfp28-6x100g-qsfp28-managed-l3": "Fabric Switch (Leaf) - Managed L3",
    "component.switch-24x40g-qsfp-plus-4x100g-qsfp28-managed-l3": "Aggregation Switch (3-tier bridge) - Managed L3",
    "component.switch-32x100g-qsfp28-managed-l3": "Backbone Switch (100G) - Managed L3",
    "component.switch-32x200g-qsfp56-managed-l3": "Backbone Switch (200G) - Managed L3",
    "component.switch-2x25g-sfp28-32x400g-qsfp-dd-managed-l3": "Backbone Switch (400G) - Managed L3",
    "component.gateway-8x1g-router": "Basic Router",
    "component.gateway-4x1g-2x10g-router": "Home Router",
    "component.gateway-8x10g-2x25g-router-firewall": "Fiber Router + Firewall",
    "component.gateway-16x25g-2x100g-border-router": "Big Border Router",
    "component.gateway-16x100g-8x400g-hyperscale-edge": "Core Border Router",
    "component.security-2x1g-rj45-2x10g-sfp-plus": "Entry Security Appliance",
    "component.security-4x10g-sfp-plus": "Business Security Appliance",
    "component.security-2x10g-sfp-plus-2x25g-sfp28": "Datacenter Security Appliance",
    "component.security-4x100g-qsfp28": "Border Security Appliance",
    "component.siem-2u": "Audit Recorder",
    "component.edd-1x1g-wall-ont": "",
    "component.edd-2x10g-colo-cross-connect": "",
    "component.edd-2x1g-residential-gateway": "",
    "component.edd-4x100g-mmr-cross-connect": "",
    "component.edd-4x25g-1u-fibre-cross-connect": "",
    "component.patch-panel-pp-24-patch-panel": "",
    "component.switch-48x10g-4x40g-tor-mlag": "",
    "component.switch-24x1g-4x10g-mlag": "",
    "component.switch-48x1g-4x10g-mlag": "",
    "component.switch-48x25g-6x100g-leaf-mlag": "",
    "component.arm-dual-socket-1u": "",
    "component.balanced-x86-dual-socket-2u": "",
    "component.memory-optimised-1u": "",
});

const entityTranslations: Readonly<Record<LanguageMode, EntityTranslations>> = Object.freeze({
    plain: Object.freeze({
        ...sharedEntityTranslations,
        ...PLAIN_COMPONENT_NAME_TRANSLATIONS,
        "zone.az-2": "Reliability area 2",
        "zone.az-1": "Reliability area 1",
        "category.switches": "Network switches",
        "category.gateways": "Network gateways",
        "category.security": "Security devices",
        "category.patch panels": "Patch panels",
    }),
    technical: sharedEntityTranslations,
});

export function translateEntity(mode: LanguageMode, kind: EntityKind, id: string, fallback: string): string {
    const normalizedId = kind === "category" ? id.toLowerCase() : id;
    const translated = entityTranslations[mode][`${kind}.${normalizedId}`];
    return translated === undefined || translated.trim() === "" ? fallback : translated;
}

const runtimeMessages: Readonly<Record<LanguageMode, EntityTranslations>> = Object.freeze({
    plain: Object.freeze({
        "Map unavailable.": "The selected room layout is unavailable.",
        "Unknown target rack.": "The selected rack is unavailable.",
        "Unknown component type.": "The selected equipment is unavailable.",
        "This component is reserved for fixed map equipment.": "This built-in equipment cannot be added manually.",
        "This component type is not supported by the target rack.": "This equipment is not supported by this rack.",
        "These rack units are reserved by fixed map equipment.": "This space is occupied by built-in equipment.",
        "These rack units overlap another component.": "This space is already occupied.",
        "This placement is not valid.": "This equipment cannot be placed here.",
        "Create or import a plan first.": "Create or open a plan first.",
        "The selected map or component is unavailable.": "The selected layout or equipment is unavailable.",
        "The component can no longer be found.": "The equipment can no longer be found.",
        "There is no compatible free space in this rack.": "There is no suitable free space in this rack.",
        "Plan created.": "Plan created.",
        "Plan imported.": "Plan opened.",
        "Plan renamed.": "Plan renamed.",
        "Scenario updated.": "Scenario updated.",
        "The selected scenario is unavailable for this map.":
            "This scenario is not available for the selected room layout.",
        "Rack settings updated.": "Rack settings updated.",
        "The selected rack is unavailable.": "The selected rack is unavailable.",
        "Rack names must contain between 1 and 80 characters.": "Rack names must contain between 1 and 80 characters.",
        "All user placements were removed.": "All added equipment was removed.",
        "Local name removed.": "Equipment name removed.",
        "Local name updated.": "Equipment name updated.",
        "Component removed.": "Equipment removed.",
        "Nothing to undo.": "There is nothing to undo.",
        "Change undone.": "Last change undone.",
        "Nothing to redo.": "There is nothing to redo.",
        "Change restored.": "Change restored.",
        "Portable .utp file exported.": "Plan copy downloaded.",
        "Last browser workspace restored.": "Your last plan was restored from this browser.",
        "Select a file with the .utp extension.": "Choose a saved .utp plan file.",
        "The plan could not be imported.": "The saved plan could not be opened.",
    }),
    technical: Object.freeze({}),
});

export function translateRuntimeText(mode: LanguageMode, value: string | undefined): string {
    if (value === undefined) {
        return "";
    }
    const exact = runtimeMessages[mode][value];
    if (exact !== undefined) {
        return exact;
    }
    if (mode === "plain") {
        const rackBoundary = /^Requires (\d+)U within the (\d+)U rack boundary\.$/.exec(value);
        if (rackBoundary !== null) {
            return `This equipment needs ${rackBoundary[1]} rack spaces inside this ${rackBoundary[2]}-space rack.`;
        }
        const placed = / placed at U(\d+)\.$/.exec(value);
        if (placed !== null) {
            return `Equipment added at position ${placed[1]}.`;
        }
        const moved = /^Component moved to U(\d+)\.$/.exec(value);
        if (moved !== null) {
            return `Equipment moved to position ${moved[1]}.`;
        }
        const duplicated = /^Component duplicated at U(\d+)\.$/.exec(value);
        if (duplicated !== null) {
            return `Equipment copied to position ${duplicated[1]}.`;
        }
        if (/^(Placement|Invalid placement|Duplicate placement ID|Unknown rack ID)/.test(value)) {
            return `The saved plan contains invalid equipment data. ${value}`;
        }
        if (/^(Unsupported|Incompatible|Unknown map)/.test(value)) {
            return `The saved plan is not compatible with this planner. ${value}`;
        }
    }
    return value;
}

interface LanguageContextValue {
    readonly mode: LanguageMode;
    readonly setMode: (mode: LanguageMode) => void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export function useUiLanguage(): LanguageContextValue & {
    readonly t: (key: MessageKey) => string;
    readonly entity: (kind: EntityKind, id: string, fallback: string) => string;
    readonly runtimeText: (value: string | undefined) => string;
} {
    const context = useContext(LanguageContext);
    if (context === null) {
        throw new Error("useUiLanguage must be used inside LanguageContext.Provider.");
    }
    return {
        ...context,
        t: (key) => translate(context.mode, key),
        entity: (kind, id, fallback) => translateEntity(context.mode, kind, id, fallback),
        runtimeText: (value) => translateRuntimeText(context.mode, value),
    };
}

export const DEFAULT_LANGUAGE_MODE: LanguageMode = "plain";
export const LANGUAGE_MODE_STORAGE_KEY = "uptime-datacenter-planner:language-mode:v1";

export function parseLanguageMode(value: string | null): LanguageMode {
    return value === "technical" ? "technical" : DEFAULT_LANGUAGE_MODE;
}
